import {
    Bot,
    Workflow,
    CalendarClock,
    MonitorSmartphone,
    PlayCircle,
    BarChart3,
    BookMarked,
    BookOpen,
    Building2,
    Clapperboard,
    ClipboardCheck,
    ClipboardList,
    Coins,
    Compass,
    FileUp,
    Gauge,
    KeyRound,
    Layers,
    Layers3,
    ListChecks,
    Headphones,
    LayoutDashboard,
    MessageSquareQuote,
    MessageSquareWarning,
    MessagesSquare,
    Palette,
    Plug,
    ScrollText,
    SendHorizonal,
    Settings2,
    SlidersHorizontal,
    ShieldCheck,
    Sparkles,
    Users,
    UsersRound,
    Waypoints,
    FlaskConical,
    HeartPulse,
    Webhook,
    History,
    PenSquare,
    Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';

import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarRail,
} from '@/components/ui/sidebar';
import { directionFor, isLocale } from '@/i18n';
import { useAuthStore, useBranding, useIsDemoMode } from '@/stores/auth';

interface NavItem {
    /** Translation key within the `nav` namespace. */
    labelKey: string;
    to: string;
    icon: LucideIcon;
    /** Any one of these permissions reveals the item. */
    permissions?: string[];
    /** Hidden unless the installation is in demonstration mode (§11.2). */
    demoOnly?: boolean;
}

interface NavSection {
    labelKey?: string;
    items: NavItem[];
}

/**
 * The navigation tree.
 *
 * Every item here links to a screen that works. Nothing renders disabled: a
 * control that cannot be pressed teaches the person using it to distrust the
 * other controls, and an evaluator cannot tell a deliberate placeholder from a
 * broken feature. An item either earns its place in the sidebar or is not in it.
 *
 * Two filters decide what an individual sees — the permissions they hold, and
 * whether this installation is a demonstration. Both hide rather than disable.
 */
const SECTIONS: NavSection[] = [
    {
        items: [
            { labelKey: 'dashboard', to: '/dashboard', icon: LayoutDashboard },
            { labelKey: 'showcase', to: '/showcase', icon: Compass },
            { labelKey: 'guide', to: '/guide', icon: BookOpen },
        ],
    },
    {
        labelKey: 'operations',
        items: [
            {
                labelKey: 'customers',
                to: '/customers',
                icon: Users,
                permissions: ['customers.view'],
            },
            {
                labelKey: 'segments',
                to: '/customers/segments',
                icon: Layers3,
                permissions: ['customers.view'],
            },
            {
                labelKey: 'import',
                to: '/customers/import',
                icon: FileUp,
                permissions: ['customers.import'],
            },
            {
                labelKey: 'complaints',
                to: '/complaints',
                icon: MessageSquareWarning,
                permissions: [
                    'complaints.view.own',
                    'complaints.view.team',
                    'complaints.view.all',
                ],
            },
            {
                labelKey: 'cc_console',
                to: '/contact-centre',
                icon: Headphones,
                permissions: ['cc.console.use'],
            },
            {
                labelKey: 'cc_wallboard',
                to: '/contact-centre/wallboard',
                icon: Gauge,
                permissions: ['cc.wallboard.view'],
            },
            {
                labelKey: 'cc_reporting',
                to: '/contact-centre/reporting',
                icon: BarChart3,
                permissions: ['cc.wallboard.view'],
            },
            {
                labelKey: 'queue',
                to: '/queue',
                icon: ClipboardList,
                permissions: ['queue.analytics.view', 'queue.tickets.view'],
            },
            {
                labelKey: 'queue_appointments',
                to: '/queue/appointments',
                icon: CalendarClock,
                permissions: ['queue.appointments.view'],
            },
            {
                labelKey: 'mex_dashboard',
                to: '/measurement',
                icon: Sparkles,
                permissions: ['measurement.analytics.view'],
            },
            {
                labelKey: 'mex_verbatims',
                to: '/measurement/verbatims',
                icon: MessageSquareQuote,
                permissions: ['measurement.verbatims.view'],
            },
            {
                labelKey: 'mex_actions',
                to: '/measurement/actions',
                icon: ClipboardCheck,
                permissions: [
                    'measurement.actions.view',
                    'measurement.actions.manage',
                ],
            },
            {
                labelKey: 'reports_executive',
                to: '/reports/executive',
                icon: Gauge,
                permissions: ['reports.view'],
            },
            {
                labelKey: 'reports',
                to: '/reports',
                icon: BarChart3,
                permissions: ['reports.view'],
            },
        ],
    },
    {
        labelKey: 'administration',
        items: [
            {
                labelKey: 'users',
                to: '/admin/users',
                icon: UsersRound,
                permissions: ['users.view', 'roles.manage'],
            },
            {
                labelKey: 'branding',
                to: '/admin/branding',
                icon: Palette,
                permissions: ['branding.manage'],
            },
            {
                labelKey: 'view_layouts',
                to: '/admin/view-layouts',
                icon: Layers,
                permissions: ['view_layouts.manage'],
            },
            {
                labelKey: 'complaint_configuration',
                to: '/admin/complaint-configuration',
                icon: SlidersHorizontal,
                permissions: [
                    'complaints.routing.manage',
                    'complaints.sla.manage',
                    'complaints.categories.manage',
                ],
            },
            {
                labelKey: 'cc_configuration',
                to: '/contact-centre/configuration',
                icon: Headphones,
                permissions: ['cc.routing.manage', 'cc.agents.manage'],
            },
            {
                labelKey: 'cc_chatbot',
                to: '/contact-centre/chatbot',
                icon: Bot,
                permissions: ['cc.chatbot.manage'],
            },
            {
                labelKey: 'cc_ivr',
                to: '/contact-centre/ivr',
                icon: Workflow,
                permissions: ['cc.ivr.manage'],
            },
            {
                labelKey: 'cc_licensing',
                to: '/contact-centre/licensing',
                icon: Coins,
                permissions: ['cc.licensing.manage'],
            },
            {
                labelKey: 'queue_configuration',
                to: '/queue/configuration',
                icon: SlidersHorizontal,
                permissions: ['queue.services.manage', 'queue.branches.manage'],
            },
            {
                labelKey: 'queue_hardware',
                to: '/queue/hardware',
                icon: MonitorSmartphone,
                permissions: ['queue.hardware.manage'],
            },
            {
                labelKey: 'queue_simulator',
                to: '/queue/simulator',
                icon: PlayCircle,
                permissions: ['queue.tickets.manage'],
            },
            {
                labelKey: 'mex_surveys',
                to: '/measurement/surveys',
                icon: ListChecks,
                permissions: ['surveys.view', 'surveys.design'],
            },
            {
                labelKey: 'mex_distributions',
                to: '/measurement/distributions',
                icon: SendHorizonal,
                permissions: ['surveys.distribute'],
            },
            {
                labelKey: 'mex_journeys',
                to: '/measurement/journeys',
                icon: Waypoints,
                permissions: ['surveys.journeys.manage'],
            },
            {
                labelKey: 'mex_simulators',
                to: '/measurement/channels',
                icon: MessagesSquare,
                permissions: ['surveys.distribute'],
            },
            {
                labelKey: 'settings',
                to: '/admin/settings',
                icon: Settings2,
                permissions: ['settings.manage'],
            },
            {
                labelKey: 'audit',
                to: '/admin/audit',
                icon: ScrollText,
                permissions: ['audit.view'],
            },
            {
                labelKey: 'health',
                to: '/admin/health',
                icon: Gauge,
                permissions: ['system.health.view'],
            },
            {
                labelKey: 'ai',
                to: '/admin/ai',
                icon: ShieldCheck,
                permissions: ['ai.settings.manage'],
            },
            {
                labelKey: 'knowledge',
                to: '/admin/knowledge',
                icon: BookOpen,
                permissions: ['ai.knowledge.manage'],
            },
            {
                labelKey: 'ai_usage',
                to: '/admin/ai-usage',
                icon: Coins,
                permissions: ['ai.usage.view'],
            },
            {
                labelKey: 'integrations',
                to: '/admin/integrations',
                icon: Plug,
                permissions: ['integrations.view', 'integrations.manage'],
            },
            {
                labelKey: 'integrations_health',
                to: '/admin/integrations/health',
                icon: HeartPulse,
                permissions: ['integrations.view', 'integrations.manage'],
            },
            {
                labelKey: 'integrations_webhooks',
                to: '/admin/integrations/webhooks',
                icon: Webhook,
                permissions: ['integrations.view', 'integrations.manage'],
            },
            {
                labelKey: 'integrations_simulators',
                to: '/admin/integrations/simulators',
                icon: FlaskConical,
                permissions: ['integrations.view', 'integrations.manage'],
            },
            {
                labelKey: 'reports_builder',
                to: '/reports/builder/new',
                icon: PenSquare,
                permissions: ['reports.build'],
            },
            {
                labelKey: 'reports_schedules',
                to: '/reports/schedules',
                icon: CalendarClock,
                permissions: ['reports.schedule'],
            },
            {
                labelKey: 'reports_runs',
                to: '/reports/history',
                icon: History,
                permissions: ['reports.view'],
            },
            {
                labelKey: 'integrations_keys',
                to: '/admin/integrations/api-keys',
                icon: KeyRound,
                permissions: ['integrations.keys.manage'],
            },
            {
                labelKey: 'events',
                to: '/admin/events',
                icon: Zap,
                permissions: ['system.health.view'],
            },
            {
                labelKey: 'api_docs',
                to: '/docs/api',
                icon: BookMarked,
            },
            {
                labelKey: 'compliance',
                to: '/admin/compliance',
                icon: Building2,
                permissions: ['rfp.matrix.view'],
            },
            {
                labelKey: 'demo',
                to: '/admin/demo',
                icon: Clapperboard,
                permissions: ['demo.control'],
                demoOnly: true,
            },
        ],
    },
];

export function AppSidebar() {
    const { t, i18n } = useTranslation('nav');
    const branding = useBranding();
    const isDemoMode = useIsDemoMode();
    const canAny = useAuthStore((state) => state.canAny);
    const location = useLocation();

    const visibleSections = SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter(
            (item) =>
                (item.permissions === undefined ||
                    canAny(...item.permissions)) &&
                (item.demoOnly !== true || isDemoMode),
        ),
    })).filter((section) => section.items.length > 0);

    return (
        /*
         * The side follows the reading direction. The sidebar primitive pins
         * itself with physical `left-0` / `right-0` rather than logical
         * properties, so it stays on the physical left under `dir="rtl"` unless
         * it is told otherwise — which put the navigation on the wrong side of
         * every Arabic screen while the content beside it flowed correctly.
         *
         * Derived from the active language rather than passed in by each caller,
         * so switching language moves it and nobody has to remember.
         */
        <Sidebar
            collapsible="icon"
            /*
             * Named for the guided tour to spotlight. A `data-tour` key rather
             * than a CSS selector: reordering the navigation must not silently
             * point the tour at the wrong thing.
             */
            data-tour="sidebar"
            side={
                directionFor(isLocale(i18n.language) ? i18n.language : 'ar') === 'rtl'
                    ? 'right'
                    : 'left'
            }
            aria-label={t('main_navigation')}
        >
            <SidebarHeader>
                {/*
                 * The logo alone — the wordmark already carries the Bank's name
                 * in both languages, so repeating it as text beside the picture
                 * said the same thing twice and crowded a narrow column.
                 *
                 * Which logo depends on the room available. Expanded, the full
                 * lockup fits and is the identity as designed. Collapsed to a
                 * 48px rail there is no width for something three and a third
                 * times wider than it is tall, so the square mark takes over.
                 * Both are rendered and swapped by the sidebar's own collapse
                 * state rather than by a resize listener, so there is no frame
                 * in which neither is showing.
                 */}
                <div className="flex items-center justify-center px-2 py-2">
                    {branding?.logo_url ? (
                        <img
                            src={branding.logo_url}
                            alt={branding.app_name}
                            className="h-9 w-auto object-contain group-data-[collapsible=icon]:hidden"
                        />
                    ) : (
                        <span className="text-sm font-semibold group-data-[collapsible=icon]:hidden">
                            {branding?.short_name ?? 'DB-CX'}
                        </span>
                    )}

                    {branding?.logo_mark_url ? (
                        <img
                            src={branding.logo_mark_url}
                            alt={branding.app_name}
                            className="hidden size-8 shrink-0 object-contain group-data-[collapsible=icon]:block"
                        />
                    ) : null}
                </div>
            </SidebarHeader>

            <SidebarContent>
                {visibleSections.map((section, index) => (
                    <SidebarGroup key={section.labelKey ?? `section-${index}`}>
                        {section.labelKey && (
                            <SidebarGroupLabel>
                                {t(section.labelKey)}
                            </SidebarGroupLabel>
                        )}

                        <SidebarMenu>
                            {section.items.map((item) => (
                                <SidebarMenuItem
                                    key={item.to}
                                    data-tour={`nav-${item.labelKey}`}
                                >
                                    <SidebarMenuButton
                                        asChild
                                        isActive={location.pathname.startsWith(
                                            item.to,
                                        )}
                                        tooltip={t(item.labelKey)}
                                    >
                                        <NavLink to={item.to}>
                                            <item.icon aria-hidden="true" />
                                            <span>{t(item.labelKey)}</span>
                                        </NavLink>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            ))}
                        </SidebarMenu>
                    </SidebarGroup>
                ))}
            </SidebarContent>

            <SidebarRail />
        </Sidebar>
    );
}
